import React, { useCallback, useMemo } from 'react';
import { pick } from 'ramda';
import { FormSpy } from 'react-final-form';
import Form from 'js/components/common/Form';
import { useTranslations } from 'js/hooks/i18n';
import { I18n } from 'js/components/common/I18n';
import { useUserSelector } from 'js/store/hooks';
import { getBonusTitle } from 'js/utils/bonus.util';
import { Button } from 'js/components/common/Button';
import { useFormatter } from 'js/hooks/useFormatter';
import { Loader } from 'js/components/common/Loader';
import { ManagedPopup } from 'js/components/common/Popup';
import { Input } from 'js/components/common/Fields/Input';
import { WebContent } from 'js/components/system/WebContent';
import { useOnClose } from 'js/components/common/PopupManager';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { ErrorMessage } from 'js/components/common/ErrorMessage';

import style from './BonusBuyInPopup.scss';

import * as hooks from './hooks';

export const popupClassNames = {
    inner: style.popupInner
};

export const BonusBuyInPopup = ({ title, bonusCode, buyInWebContentId, termAndConditionWebContentId, ...props }) => {
    const translate = useTranslations('playtech.mobile.system.');
    const formatter = useFormatter();
    const onClose = useOnClose();
    const { currencyCode, lang } = useUserSelector(pick(['currencyCode', 'lang']));
    const balance = hooks.useBalance();
    const { isLoading, error, bonusTemplate } = hooks.useBonusBuyInTemplate({ bonusCode });
    const amounts = hooks.useAmounts({ bonusTemplate });
    const allowedAmount = hooks.useAllowedAmount({ balance, bonusTemplate, amounts });
    const validationConfig = hooks.useValidationConfig({ balance, amounts, allowedAmount });
    const showTermsPopup = hooks.useShowTermsPopup({ termAndConditionWebContentId });
    const getBenefits = hooks.useGetBenefits({ bonusTemplate });
    const getBuyInBonus = hooks.useGetBuyInBonus({ translate, bonusCode, getBenefits });
    const popupTitle = useMemo(() => {
        if (isLoading) {
            return '';
        }

        return title
            || bonusTemplate && getBonusTitle({ bonusTemplate }, { lang })
            || translate(`bonus.${bonusCode}`);
    }, [bonusTemplate, lang, bonusCode, title, translate, isLoading]);

    let errorMessage = error;

    const onSubmit = useCallback(({ amount }) => {
        getBuyInBonus({ amount });
        onClose();
    }, [
        getBuyInBonus,
        onClose
    ]);

    if (bonusTemplate && !amounts.length) {
        errorMessage = translate('buyIn.noAmounts');
    }

    if (errorMessage) {
        return (
            <ManagedPopup
                {...props}
                title={popupTitle}
                classNames={popupClassNames}
                onClose={onClose}
            >
                <ErrorMessage
                    animate={true}
                    error={errorMessage}
                    className={style.error}
                />
            </ManagedPopup>
        );
    }

    if (!bonusTemplate) {
        return (
            <ManagedPopup
                {...props}
                classNames={{ ...popupClassNames, header: style.header }}
                onClose={onClose}
            >
                <Loader />
            </ManagedPopup>
        );
    }

    return (
        <ManagedPopup
            {...props}
            title={popupTitle}
            classNames={popupClassNames}
            onClose={onClose}
        >
            <Form
                onSubmit={onSubmit}
                validationConfig={validationConfig}
                fieldsToReplaceComma={['amount']}
                render={({ handleSubmit }) => (
                    <form onSubmit={event => event.preventDefault()}>
                        <div className={style.buyInBonusTitle}>{translate('buyInWith')}</div>
                        <Input
                            type="text"
                            name="amount"
                            inputMode="decimal"
                            className={style.buyInInput}
                            initialValue={allowedAmount}
                            placeholder={translate('amount')}
                            aria-label={translate(['amount', 'playtech.mobile.system.input.aria-label'])}
                            currencySymbol={formatter.getCurrencySymbol(currencyCode)}
                        />
                        <FormSpy>
                            {({ errors: { amount: hasError }, values: { amount: value } }) => {
                                if (hasError) {
                                    return null;
                                }

                                const bonuses = getBenefits({ value });

                                if (bonuses.length === 0) {
                                    return null;
                                }

                                return <div className={style.benefit}>
                                    {translate('receiveBonus')}
                                    &nbsp;
                                    {bonuses.length > 1
                                        ? `${bonuses[0]}% ${translate('and')} ${formatter.formatCurrency(bonuses[1], { currencyCode })}`
                                        : formatter.formatCurrency(bonuses[0], { currencyCode })
                                    }
                                </div>;
                            }}
                        </FormSpy>
                        <div className={style.terms}>
                            <Checkbox
                                initialValue={false}
                                className={style.checkbox}
                                name="terms"
                                label={(
                                    <>
                                        <I18n t={translate} tKey={'agreeWithTC'} />&nbsp;
                                        <a href="#" onClick={showTermsPopup}>{translate('tc')}</a>
                                    </>
                                )}
                            />
                        </div>
                        <div className={style.buttons}>
                            <Button
                                type="button"
                                view="primary"
                                color="negative"
                                onClick={onClose}
                                className={style.button}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button
                                type="button"
                                view="primary"
                                onClick={handleSubmit}
                                className={style.button}
                            >
                                {translate('buyIn')}
                            </Button>
                        </div>
                    </form>
                )}
            />
            {buyInWebContentId && <WebContent id={buyInWebContentId} />}
        </ManagedPopup>
    );
};
